import Tippy from '@tippyjs/react';
import React, {useContext, useState} from 'react';
import {IVehicleSearch} from '../../interfaces/VehicleSearch';
import { translateService } from '../../services/TranslateService';
import Translate from 'react-translate-component';
import styles from "./main.module.scss";
import InfoIcon from '@mui/icons-material/Info';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { cx, priceFormat } from './util';
import Modal from 'react-modal';
import Select from 'react-select';
import { useSelector, useDispatch } from 'react-redux';
import {GlobalStore} from "../../reduxStore";
import { DataContext } from '../../contexts/dataContext';
import {CustomerTextInput} from "../CustomerInput/CustomerInput";
import {useValidAddDriverNumber, useValidFullName} from "../../hooks/useCustomerHooks";

interface LabeledInputProps {
  tooltip?: React.ReactElement | undefined,
  children?: React.ReactElement;
  extraContent?: React.ReactElement;
  id?:string;
  value?:string | number;
  defaultChecked?:boolean
  type?:string;
  name?:string;
  disabled?:boolean;
  onChange?: any;
  checked?:boolean;
  defaultValue?:any;
  className?: string;
  readOnly?: boolean;
}

function LabeledInput({ tooltip = undefined, children, extraContent, ...props }: LabeledInputProps) {
  return <div className="w-100 mx-auto">
    <input {...props} className={props.className ? props.className : styles.r24SSCheckboxInput} />
    <div className="d-block ml-4 mb-2">
      <label htmlFor={props.id} className="d-inline ml-1">
        {children}
      </label>
      {tooltip && <>
        &nbsp;<Tippy
          zIndex={2}
          content={tooltip}>
        <InfoIcon className={styles.infoIcon} />
      </Tippy>
      </>}
      {extraContent}
    </div>
  </div>;
}

interface PropsInterface {
  vehicle: IVehicleSearch;
  back: Function, next: Function;
  reservationLength : number;
}

export default function SelectServices({ vehicle, back, next, reservationLength}: PropsInterface) {
  const dataContext = useContext(DataContext);
  const [additionalDriverPopup, showAdditionalDriverPopup] = useState(false);
  const [errorStyle, setErrorStyle] = useState('');
  const [kmRangeErrorStyle, setKmRangeErrorStyle] = useState('');
  const coupon = useSelector((s: GlobalStore) => s.chauffeurReducer.coupon);
  const language = useSelector((s: GlobalStore) => s.chauffeurReducer.deviceLanguage) || 'en';
  const couponValid = !!coupon?.valid && vehicle.pricingData.coupon && !vehicle.onsale;
  const { services, changeServices } = useContext(DataContext);
  const {additionalDriverName, setAdditionalDriverName, addDriverNameErrorString, handleAddDriverNameFieldErr,
    addDriverNameFieldRef} = useValidFullName( services.addDriverName || '',
      "additionalDriverNameField", !(services.additionalDriver));
  const {addDriverNumber, addDriverNumberErrorString, setAddDriverNumber, handleAddDriverNumFieldErr,
    addDriverNumFieldRef} = useValidAddDriverNumber(services.addDriverNumber ||'',
      'addDriverNumberField', dataContext.user?.phoneNumber || '', !(services.additionalDriver));
  const [selectServicesSubmit, setSelectServicesSubmit] = useState(false);
  const dispatch = useDispatch();

  function checkCoupon(){
    return (!couponValid ? vehicle.pricingData.base : vehicle.pricingData.withCoupon);
  }

  function validate() {
    if (!vehicle.availabilityData?.available) {

      dispatch({
        type: 'SET_RESPONSE_MODAL',
        payload: {text: translateService.translate('vehicleSearch1.vehicleIsAlreadyReserved'),
          scrollToTop: true}
      });
    } else {
      setSelectServicesSubmit(true);
      setKmRangeErrorStyle('')
      setErrorStyle('')
      if (services.export) {
        if(!services.exportDestination) {
          setErrorStyle(styles.invalidForm);
          return;
        }

        if(services.kmRange===0)  {//unlimited
          setTimeout(() => {
            document.getElementById('kmRange')?.scrollIntoView({
              behavior: 'smooth',block: 'center'});
          }, 0);
          setKmRangeErrorStyle(styles.invalidForm);
          return;
        }
      } else {
        if(vehicle.pricingData.base.unlimitedKilometers!==null && services.kmRange===400)  {//rural
          setTimeout(() => {
            document.getElementById('kmRange')?.scrollIntoView({
              behavior: 'smooth',block: 'center'});
          }, 0);

          setKmRangeErrorStyle(styles.invalidForm);
          return;
        }
      }

      let addDriverErr: null | string = null;
      const checkValidationFields = () => {
        // uses the hook functions to check errors and return component names for autoscroll if err exists
        if(handleAddDriverNameFieldErr()) { return handleAddDriverNameFieldErr() }
        if(handleAddDriverNumFieldErr()) { return handleAddDriverNumFieldErr() }
        return null
      }
      if(services.additionalDriver) {
        addDriverErr = checkValidationFields();
        if (addDriverErr) {
          let inputField = document.querySelector('[name="' + addDriverErr + '"]');
          inputField?.scrollIntoView({behavior: "smooth", block: "center"})
          return;
        }
      }
      next();
    }
  }

  React.useEffect(() => {
    if (!services.additionalDriver && (services.addDriverName !== '' || services.addDriverNumber !== '') ) {
      // clear additional driver info if additional driver option not selected
      changeServices({...services, "addDriverName": '', "addDriverNumber": ''})
      setAddDriverNumber('');
      setAdditionalDriverName('');
    }
  }, [services.additionalDriver, services.addDriverName, services.addDriverNumber,
    changeServices, services, setAddDriverNumber, setAdditionalDriverName])

  let countries :string[] = [];

  const availableCountriesBaltia: Record<string, string[]> = {
    "fi": ["Viro", "Latvia", "Liettua"],
    "en": ["Estonia", "Latvia", "Lithuania"]
  };
  const availableCountriesScandinavia: Record<string, string[]> = {
    "fi": ["Ruotsi", "Norja", "Tanska"],
    "en": ["Sweden", "Norway", "Denmark"]
  };
  const availableCountriesCentralEurope: Record<string, string[]> = {
    "fi": ["Saksa", "Ranska", "Itävalta", "Sveitsi", "Belgia", "Alankomaat", "Luxemburg"],
    "en": ["Germany", "France", "Austria", "Switzerland", "Belgium", "Netherlands", "Luxembourg"]
  };

  if (vehicle.vehicleData.exportPermits.balticStates)
    countries.push(...availableCountriesBaltia[language])
  if (vehicle.vehicleData.exportPermits.europe)
    countries.push(...availableCountriesCentralEurope[language])
  if (vehicle.vehicleData.exportPermits.scandinavia)
    countries.push(...availableCountriesScandinavia[language])

  const countryOptions = countries.map((country) => ({value:country,label:country}));

  Modal.setAppElement('body');

  const setService = (name: string, value: any) => {
    changeServices({...services, [name]: value });
  }

  const eurosPerExtraKm = '0.20'; //TODO fetch these constants from the backend instead
  const treePrice = 7.90;  //TODO fetch these constants from the backend instead

  const modalPopupStyle = {
    content: {
      width: 'auto',
      minWidth: '200px',
      maxWidth: '769px',
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'rgba(0,0,0,0.7)',
      color: 'white',
      textAlign: 'center',
    },
    overlay: {
      zIndex: '1',
    },
  };

  return <>
    <Modal style={Object.assign(modalPopupStyle)}  isOpen={additionalDriverPopup}>
      <div className='icon-top-heading text-dark'>
        {translateService.translate('vehicleSearch1.note')}
      </div>
      <hr />
      <span><Translate content={'vehicleSearch.services.addAdditionalDriverToolTip'} /> </span>
      <button onClick={() => showAdditionalDriverPopup(false)} className='btn btn-primary btn-block mt-2'>{translateService.translate('driverModal.ok')}</button>
    </Modal>
    <div className={cx(styles.r24FullWidth, styles.r24TopMargin10)}>
      <div className={styles.r24FullWidth}>
        <form action="#">
          <div className={cx(styles.r24FullWidth, styles.noBorderAround, styles.r24BottomMargin10)} id="kmRange">
            <div className={cx(styles.r24FullWidth, kmRangeErrorStyle)} onChange={(e) => setService('kmRange', Number((e.target as HTMLInputElement).value))}>
              <span className={styles.reservationInfo}>
                <Translate content={'vehicleSearch.services.distance'} />&nbsp;
                <Tippy zIndex={2}
                       content={<Translate content={'vehicleSearch.services.kmInfoText'} />}
                >
                  <InfoIcon className={styles.infoIcon} />
                </Tippy>
                <a className={cx("btn btn-info btn-sm py-0 mb-2 px-3")} style={{marginLeft: 5}} target="_blank" rel="noopener noreferrer"
                   href={language !== 'en' ?
                       "https://www.24rent.fi/ajomatka/"
                       :
                       "https://www.24rent.fi/en/distance/"
                   }>
                  <Translate content={'vehicleSearch.services.moreInformation'} />
                </a>
              </span>
              { !dataContext.oneWayRes ?
                  <>
                    <LabeledInput // 100km always visible
                        tooltip={<Translate content={"vehicleSearch.services.kmDisclaimer"} />} type="radio" name="kmRange"
                        defaultChecked={services.kmRange===100} value={100} id="cityRange{vehicle.id}" >
                      <>
                        <Translate content={'vehicleSearch.services.cityRange'} />&nbsp;
                        <strong>{100 * reservationLength} km</strong>.&nbsp;
                        <Translate content={'vehicleSearch.services.itIsIncludedInPrice'} />
                      </>
                    </LabeledInput>
                    {(services.export || vehicle.pricingData.base.unlimitedKilometers === null) &&
                        <span>
                          <LabeledInput // 400km visible if export or no unlimitedKmOffer* AND 400km price not zero
                              tooltip={<Translate content={"vehicleSearch.services.kmDisclaimer"} />} type="radio" name="kmRange"
                              defaultChecked={services.kmRange===400} value={400} id="rural{vehicle.id}">
                            <>
                              <Translate content={"vehicleSearch.services.rural"}/> <strong>{400 * reservationLength} km&nbsp;</strong>
                              <span>+&nbsp;
                                { priceFormat((checkCoupon()!.pricePerKilometer / 100) * 300 * reservationLength)}&nbsp;€
                              </span>
                            </>
                          </LabeledInput>
                        </span>
                    }
                    {( vehicle.pricingData.base.unlimitedKilometers !== null && !services.export) &&
                        <span>
                          <LabeledInput // unlimited kms visible if export or not unlimitedOffer* AND unlimited price not zero
                              tooltip={<Translate content={"vehicleSearch.services.unlimitedKmDisclaimer"} />} type="radio" name="kmRange"
                              defaultChecked={services.kmRange===0} value={0} id="unlimitedCampaign{vehicle.id}" >
                            <>
                              <strong><Translate content={'vehicleSearch.services.unlimitedIncluded'} />  </strong>
                              <span>+
                                { priceFormat(checkCoupon()!.unlimitedKilometers / 100 * reservationLength)}€
                              </span>
                            </>
                          </LabeledInput>
                        </span>
                    }
                    <p className={styles.priceperkm}>
                      <Translate content={'vehicleSearch.services.pricePerKm'} />  {eurosPerExtraKm} € / km.
                    </p>
                    <p className={styles.priceperkm}>
                      <Translate content={'vehicleSearch.services.kmDisclaimer'} />
                    </p>
                    {kmRangeErrorStyle ? (
                        <div className={cx(styles.errorText,"mt-1 ml-4")}>
                          {translateService.translate( services.export ? 'vehicleSearch1.unlimitedUnavailable' : 'vehicleSearch1.ruralUnavailable')}
                        </div>
                    ) : null}
                  </>
                  : // one-way res - only 1 set option
                  <>
                    <LabeledInput tooltip={<Translate content={"vehicleSearch.services.kmDisclaimer"} />} type="radio" name="kmRange"
                                  defaultChecked={services.kmRange===100} value={100} id="oneway{vehicle.id}" >
                      <>
                        <Translate content={'vehicleSearch.services.oneWayKmsDescription'} />&nbsp;
                        <Translate content={'vehicleSearch.services.itIsIncludedInPrice'} />
                      </>
                    </LabeledInput>
                    <p className={styles.priceperkm}>
                      <Translate content={'vehicleSearch.services.pricePerKm'} />  {eurosPerExtraKm} € / km.
                    </p>
                    <p className={styles.priceperkm}>
                      <Translate content={'vehicleSearch.services.kmDisclaimer'} />
                    </p>

                  </>
              }
            </div>
          </div>
          <div className={cx(styles.r24FullWidth, styles.lightGrayline, styles.r24TopBottomMargin5)}></div>
          <div className={cx(styles.r24FullWidth, styles.noBorderAround, styles.r24BottomMargin10)} id="insurancesAndDeductibles">
            <div className={cx(styles.r24FullWidth, styles.additionalInsurances)}>
              <div onChange={(e) => {
                e.persist();
                let insurance = services.insurance ?? "";
                if((e.target as HTMLInputElement).value === '1053') {
                  if (insurance.includes('1028')) {
                    insurance=insurance.replace('1028,','');
                  }
                  if (insurance.includes('1053')) {
                    insurance=insurance.replace('1053,','')
                  } else {
                    insurance += (e.target as HTMLInputElement).value+","
                  }
                }

                if((e.target as HTMLInputElement).value === '1028') {
                  if (insurance.includes('1053')) {
                    insurance=insurance.replace('1053,','');
                  }
                  if (insurance.includes('1028')) {
                    insurance=insurance.replace('1028,','')
                  } else {
                    insurance += (e.target as HTMLInputElement).value+","
                  }
                }
                setService('insurance', insurance);
              }}
              >
                <span className={styles.reservationInfo}> <Translate content={'vehicleSearch.services.insurancesAndDeductibles'} />
                <a className={cx("btn btn-info btn-sm py-0 mb-2 px-3")} style={{marginLeft: 5}} target="_blank" rel="noopener noreferrer"
                   href={language !== 'en' ?
                       "https://www.24rent.fi/vakuutusturva-ja-omavastuu/"
                       :
                       "https://www.24rent.fi/en/insurance-coverage-and-deductible/"
                   }>
                    <Translate content={'vehicleSearch.services.moreInformation'} />
                </a>
                </span>

                <LabeledInput // always selected
                    type="checkbox" disabled name="insurance" defaultChecked={services.insurance.includes('1030')}
                    id="defaultDeductible{vehicle.id}" value={'1030'} className={styles.r24SSInsuranceCheckboxSelected}>
                  <>
                    <Translate content={'vehicleSearch.services.defaultDeductibleTxt'} />
                    <strong> {services.export ? vehicle.vehicleData.deductible.highDeductibleValue! / 100 * 4
                        :
                        vehicle.vehicleData.deductible.highDeductibleValue! / 100} €
                    </strong>
                  </>
                </LabeledInput>

                { vehicle.pricingData.base.midDeductible !== null ?
                      <LabeledInput // mid deductible insurance
                          tooltip={<><Translate content={'vehicleSearch.services.lowDeductionToolTip1'} />&nbsp;
                            {priceFormat(vehicle.vehicleData.deductible.highDeductibleValue! / 100 * (services.export ? 4 : 1))} €.&nbsp;
                            <Translate content={'vehicleSearch.services.lowDeductionToolTip2'} />&nbsp;
                            {priceFormat(vehicle.vehicleData.deductible.midDeductibleValue! / 100 * (services.export ? 4 : 1))} €.</>}
                          type="checkbox" name="insurance" id="midDeductible{vehicle.id}" checked={services.insurance.includes('1053')}
                          value={'1053'} readOnly={true}>
                        <>
                          <Translate content={'vehicleSearch.services.midDeductionText'} />&nbsp;
                          <strong>
                            {vehicle.vehicleData.deductible.midDeductibleValue! / 100 * (services.export ? 4 : 1)} €
                          </strong>
                          <span> +
                      <span>
                        {priceFormat( checkCoupon()!.midDeductible! / 100 *
                            (reservationLength > vehicle.servicesMaxChargeableDays['1053'] ?
                                vehicle.servicesMaxChargeableDays['1053'] : reservationLength))} €
                      </span>
                    </span>
                        </>
                      </LabeledInput> : null
                }

                { vehicle.pricingData.base.lowDeductible !== null ?
                        <LabeledInput // lower deductible insurance
                            tooltip={<><Translate content={'vehicleSearch.services.lowDeductionToolTip1'} />&nbsp;
                              {priceFormat(vehicle.vehicleData.deductible.highDeductibleValue! / 100 * (services.export ? 4 : 1))} €.&nbsp;
                              <Translate content={'vehicleSearch.services.lowDeductionToolTip2'} />&nbsp;
                              {priceFormat(vehicle.vehicleData.deductible.lowDeductibleValue! / 100 * (services.export ? 4 : 1))} €.</>}
                            type="checkbox" name="insurance" id="lowDeductible{vehicle.id}" checked={services.insurance.includes('1028')}
                            value={'1028'} readOnly={true}>
                          <>
                            <Translate content={'vehicleSearch.services.lowDeductibleTxt2'} />&nbsp;
                            <strong>
                              {vehicle.vehicleData.deductible.lowDeductibleValue! / 100 * (services.export ? 4 : 1)} €
                            </strong>
                            <span> +
                      <span>
                        {priceFormat( checkCoupon()!.lowDeductible / 100 *
                            (reservationLength > vehicle.servicesMaxChargeableDays['1028'] ?
                                vehicle.servicesMaxChargeableDays['1028']
                                :
                                reservationLength))} €
                      </span>
                    </span>
                          </>
                        </LabeledInput>
                        : null
                }


                {services.export &&
                    <p className={styles.cancellationinsurance}>
                      <Translate content={'vehicleSearch.services.exportInsurance'} />
                    </p>
                }
              </div>
            </div>
          </div>

          { vehicle.pricingData.base.windshieldInsurance !== null || vehicle.pricingData.base.tireInsurance !== null ||
          (vehicle.pricingData.base.cancellationInsurance !== null && !dataContext.oneWayRes) ?
              <>
                <div className={cx(styles.r24FullWidth, styles.lightGrayline, styles.r24TopBottomMargin5)}></div>
                <div className={cx(styles.r24FullWidth, styles.noBorderAround, styles.r24BottomMargin10)} id="additionalInsurances">
                  <div className={cx(styles.r24FullWidth, styles.additionalInsurances)}>
                    <span className={styles.reservationInfo}> <Translate content={'vehicleSearch.services.additionalInsurances2'} />
                    <a className={cx("btn btn-info btn-sm py-0 mb-2 px-3")} style={{marginLeft: 5}} target="_blank" rel="noopener noreferrer"
                       href={language !== 'en' ?
                           "https://www.24rent.fi/lisaturvat/"
                           :
                           "https://www.24rent.fi/en/additional-coverages/"
                       }>
                      <Translate content={'vehicleSearch.services.moreInformation'} />
                    </a>
                    </span>
                    {vehicle.pricingData.base.windshieldInsurance !== null &&
                        <span>
                      <LabeledInput tooltip={<Translate content={"vehicleSearch.services.windshieldToolTip"} />} type="checkbox" id="windshield{vehicle.id}"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setService('windshield', e.target.checked)} checked={services.windshield}>
                        <>
                          <Translate content={'vehicleSearch.services.windshieldInsurance'} />  +
                          <span>
                            {reservationLength > vehicle.servicesMaxChargeableDays['1026'] ?
                                <span>{priceFormat(checkCoupon()!.windshieldInsurance / 100 * vehicle.servicesMaxChargeableDays['1026'])} €</span>
                                :
                                <span>{priceFormat(checkCoupon()!.windshieldInsurance / 100 * reservationLength)} €</span>
                            }
                          </span>
                        </>
                      </LabeledInput>
                    </span>
                    }
                    {vehicle.pricingData.base.tireInsurance !== null &&
                        <span>
                      <LabeledInput tooltip={<Translate content={"vehicleSearch.services.tireInsuranceToolTip"} />} type="checkbox" id="tireinsurance{vehicle.id}"
                                    onChange={(e:React.ChangeEvent<HTMLInputElement>) => setService('tireInsurance', e.target.checked)} checked={services.tireInsurance}>
                        <>
                          <Translate content={'vehicleSearch.services.tireInsurance'} />  +
                          <span>
                            {reservationLength > vehicle.servicesMaxChargeableDays['1038'] ?
                                <span>
                                  {priceFormat(checkCoupon()!.tireInsurance / 100 * vehicle.servicesMaxChargeableDays['1038'])} €
                                </span>
                                :
                                <span>
                                  {priceFormat(checkCoupon()!.tireInsurance / 100 * reservationLength)} €
                                </span>
                            }
                          </span>
                        </>
                      </LabeledInput>
                    </span>}
                    {vehicle.pricingData.base.cancellationInsurance !== null && !dataContext.oneWayRes ?
                        <span>
                      <LabeledInput tooltip={<Translate content={"vehicleSearch.services.cancellationInsuranceToolTip"} />}
                                    type="checkbox" id="cancellationinsurance{vehicle.id}"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setService('cancellationInsurance', e.target.checked)}
                                    checked={services.cancellationInsurance} defaultValue={false}>
                        <>
                          <Translate content={'vehicleSearch.services.cancellationInsurance'} />  +
                          <span>
                            {reservationLength > vehicle.servicesMaxChargeableDays['1040'] ?
                                <span>
                                  {priceFormat(checkCoupon()!.cancellationInsurance / 100 * vehicle.servicesMaxChargeableDays['1040'])} €
                                </span>
                                :
                                <span>
                                  {priceFormat(checkCoupon()!.cancellationInsurance / 100 * reservationLength)} €
                                </span>
                            }
                          </span>
                        </>
                      </LabeledInput>
                      <p className={styles.cancellationinsurance}>
                        <Translate content={'vehicleSearch.services.cancellationInsuranceText'} />
                      </p>
                    </span> : null
                    }
                  </div>
                </div>
              </>
              : null }

          <div className={cx(styles.r24FullWidth, styles.lightGrayline, styles.r24TopBottomMargin5)}></div>
          <div className={cx(styles.r24FullWidth, styles.noBorderAround, styles.r24BottomMargin10)} id="additionalDriver">
            <div className={cx(styles.r24FullWidth)}>
              <span className={styles.reservationInfo}>
                <Translate content={'vehicleSearch.services.addAdditionalDriver'} />
                <a className={cx("btn btn-info btn-sm py-0 mb-2 px-3")} style={{marginLeft: 5}} target="_blank" rel="noopener noreferrer"
                   href={language !== 'en' ?
                       "https://www.24rent.fi/lisakuljettaja/"
                       :
                       "https://www.24rent.fi/en/additional-driver/"
                   }>
                  <Translate content={'vehicleSearch.services.moreInformation'} />
                </a>
              </span>
              <div id="driver{vehicle.id}" className={cx(styles.r24FullWidth)}>
                <LabeledInput tooltip={<Translate content={"vehicleSearch.services.addAdditionalDriverToolTip"} />}
                              checked={services.additionalDriver} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  showAdditionalDriverPopup(!services.additionalDriver);
                  if(!services.additionalDriver) {dataContext.sendGaEvent('reservation', 'AdditionalDriverPopup', '', 0)}
                  setService('additionalDriver', Boolean(e.target.checked));
                }}
                              type="checkbox" id="additionaldriver{vehicle.id}" defaultValue={false}>
                  <><Translate content={'vehicleSearch.services.addAdditionalDriver'} />  +
                    <span>
                      {reservationLength > vehicle.servicesMaxChargeableDays['1041'] ?
                          <span>
                          {priceFormat(checkCoupon()!.additionalDriver / 100 * vehicle.servicesMaxChargeableDays['1041'])} €
                        </span>
                          :
                          <span>
                          {priceFormat(checkCoupon()!.additionalDriver / 100 * reservationLength)} €
                        </span>
                      }
                    </span>
                  </>
                </LabeledInput>
                <p className={styles.additionaldriverinstructions}>
                  <Translate content={'vehicleSearch.services.addAdditionalDriverInstructions1'} />
                  <Translate content={'vehicleSearch.services.addAdditionalDriverInstructions2'} />
                </p>
              </div>

              { services.additionalDriver &&
                  <div className={cx(styles.r24FullWidthFlexNormal, styles.r24TopBottomMargin10)}
                       onChange={(e) => {console.log((e.target as HTMLInputElement).value)
                         if((e.target as HTMLInputElement).name === "additionalDriverNameField") {
                           setService('addDriverName', (e.target as HTMLInputElement).value)
                         }

                         if((e.target as HTMLInputElement).name === "addDriverNumberField") {
                           setService('addDriverNumber', (e.target as HTMLInputElement).value)
                         }}} >

                    <CustomerTextInput placeholder={translateService.translate("vehicleSearch1.driverName") + " *"}
                                       errorString={addDriverNameErrorString}
                                       value={additionalDriverName}
                                       formSubmitted={selectServicesSubmit}
                                       fieldName={addDriverNameFieldRef}
                                       setValue={setAdditionalDriverName}
                                       className={cx(styles.r24ChildFlex1, styles.grayBorderAround, styles.r24CustomerInfoRight, styles.vehicleSearch)}
                    />
                    <CustomerTextInput placeholder={translateService.translate("vehicleSearch1.driverPhone") + " *"}
                                       errorString={addDriverNumberErrorString}
                                       value={addDriverNumber}
                                       formSubmitted={selectServicesSubmit}
                                       fieldName={addDriverNumFieldRef}
                                       setValue={setAddDriverNumber}
                                       className={cx(styles.r24ChildFlex1, styles.grayBorderAround, styles.r24CustomerInfoRight, styles.vehicleSearch)}
                    />
                  </div>
              }
            </div>
          </div>
          {(vehicle.vehicleData.hasHitch || vehicle.vehicleData.hasHandtruck ) &&
              <>
                <div className={cx(styles.r24FullWidth, styles.lightGrayline, styles.r24TopBottomMargin5)}></div>
                <div className={cx(styles.r24FullWidth, styles.noBorderAround, styles.r24BottomMargin10)}>
                  <div className={cx(styles.r24FullWidth)} id="additionalServices">
                  <span className={styles.reservationInfo}>
                    <Translate content={'vehicleSearch.services.additionalServices'} />
                  </span>
                    {vehicle.vehicleData.hasHitch &&
                        <LabeledInput tooltip={<Translate content={"vehicleSearch.services.hitchToopTip"} />} type="checkbox" id="hook{vehicle.id}"
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setService('hook', e.target.checked)} checked={services.hook}>
                          <>
                            <Translate content={'vehicleSearch.services.hook'} />  +
                            <span>{priceFormat(checkCoupon()!.towHitch / 100 * reservationLength)} </span>
                            € / <Translate content={'vehicleSearch.services.day'} />
                          </>
                        </LabeledInput>
                    }
                    {vehicle.vehicleData.hasHandtruck &&
                        <LabeledInput tooltip={<Translate content={"vehicleSearch.services.handTruckToopTip"} />} type="checkbox" disabled id="handTruck{vehicle.id}"
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setService('handTruck', !e.target.checked)} checked={services.handTruck}>
                          <Translate content={'vehicleSearch.services.includesHandTruck'} />
                        </LabeledInput>
                    }
                  </div>
                </div>
              </>}
          {!dataContext.oneWayRes && (vehicle.vehicleData.exportPermits.balticStates || vehicle.vehicleData.exportPermits.scandinavia || vehicle.vehicleData.exportPermits.europe) ?
                <>
                  <div className={cx(styles.r24FullWidth, styles.lightGrayline, styles.r24TopBottomMargin5)}></div>
                  <div className={cx(styles.r24FullWidth, styles.noBorderAround, styles.r24BottomMargin10)} id="export">
                    <div className={cx(styles.r24FullWidth, styles.export)}>
                    <span className={styles.reservationInfo}><Translate content={'vehicleSearch.services.export'} />
                    <a className={cx("btn btn-info btn-sm py-0 mb-2 px-3")} style={{marginLeft: 5}} target="_blank" rel="noopener noreferrer"
                       href={language !== 'en' ?
                           "https://www.24rent.fi/24rent-autolla-ulkomaille/"
                           :
                           "https://www.24rent.fi/en/cross-border-travel/"
                       }>
                      <Translate content={'vehicleSearch.services.moreInformation'} />
                    </a>
                    </span>

                      <LabeledInput tooltip={<Translate content={"vehicleSearch.services.exportToolTip"} />} type="checkbox" id="filled-in-box{vehicle.id}"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => { console.log(e.target.checked); setService('export', Boolean(e.target.checked)) }}
                                    checked={services.export}>
                        <>
                          <Translate content={'vehicleSearch.services.exportTickBox'} />  +
                          <span>
                      {reservationLength > vehicle.servicesMaxChargeableDays['1023'] ?
                          <span>
                          {priceFormat(checkCoupon()!.export / 100 * vehicle.servicesMaxChargeableDays['1023'])} €
                        </span>
                          :
                          <span>
                          {priceFormat(checkCoupon()!.export / 100 * reservationLength)} €
                        </span>
                      }
                    </span>
                        </>
                      </LabeledInput>
                      <p>
                        <Translate content={'vehicleSearch.services.allowedCountries'} />&nbsp;
                        {vehicle.vehicleData.exportPermits.balticStates &&
                            <span>
                    <Translate content={'vehicleSearch.services.baltic'} />,&nbsp;
                  </span>}
                        {vehicle.vehicleData.exportPermits.scandinavia &&
                            <span>
                    <Translate content={'vehicleSearch.services.scandinavia'} />,&nbsp;
                  </span>}
                        {vehicle.vehicleData.exportPermits.europe &&
                            <span>
                    <Translate content={'vehicleSearch.services.europe'} />&nbsp;
                              <a target="_blank" rel="noopener noreferrer" href={language !== 'en' ?
                                  "https://www.24rent.fi/24rent-autolla-ulkomaille/"
                                  :
                                  "https://www.24rent.fi/en/cross-border-travel/"
                              }>
                      (<Translate content={'vehicleSearch.services.europelink'} />)
                    </a>
                  </span>
                        }
                      </p>
                      {services.export &&
                          <div className='mt-3'>
                            <Select
                                className={errorStyle}
                                value={services.exportDestination}
                                placeholder={translateService.translate('vehicleSearch.services.selectExportDestination')}
                                options={countryOptions}
                                onChange={v => { setService('exportDestination', v); setErrorStyle(''); }}
                                theme={(theme) => {return {
                                  ...theme,
                                  colors: {
                                    ...theme.colors,
                                    primary: '#fbb040',
                                  },
                                }}}
                            />

                            {errorStyle ? (
                                <div className={styles.errorText} style={{ marginLeft: "30px" }}>
                                  {translateService.translate('vehicleSearch1.fieldRequired')}
                                </div>
                            ) : null}
                          </div>
                      }
                    </div>
                  </div>
                </>
                : null
          }
          {vehicle.pricingData.base.hundredTwentyFiveGramsCOTwo != null &&
              <>
                <div className={cx(styles.r24FullWidth, styles.lightGrayline, styles.r24TopBottomMargin5)}></div>
                <div className={cx(styles.r24FullWidth, styles.noBorderAround, styles.r24BottomMargin10)}>
                  <div className={cx(styles.r24FullWidth)} onChange={(e:any) => setService('co2compensation', e.target.value)}>
                  <span className={styles.reservationInfo}>
                    <Translate content={'vehicleSearch.services.co2Compensation'} />&nbsp;
                    <a className={cx("btn btn-info btn-sm py-0 mb-2 px-3")} target="_blank" rel="noopener noreferrer"
                       href={language !== 'en' ?
                           "https://www.24rent.fi/paastohyvityspalvelu/"
                           :
                           "https://www.24rent.fi/carbon-footprint-offset-service/"
                       }>
                      <Translate content={'vehicleSearch.services.moreInformation'} />
                    </a>
                  </span>
                    <LabeledInput type="radio" id="co2compensation0{vehicle.id}" defaultChecked={services.co2compensation===''} name="co2compensation" value="">
                      <Translate content={'vehicleSearch.services.noCO2Compensation'} />
                    </LabeledInput>
                    <LabeledInput tooltip={<Translate content={"vehicleSearch.services.co2compensationToolTip1047"} />} type="radio"
                                  id="co2compensation1{vehicle.id}" name="co2compensation" defaultChecked={services.co2compensation==='1047'} value="1047">
                      <>
                        <Translate content={'vehicleSearch.services.miniCompensation'} />  +
                        {reservationLength > vehicle.servicesMaxChargeableDays['1047'] &&
                            <span>
                          {priceFormat(vehicle.pricingData.base.hundredTwentyFiveGramsCOTwo / 100 * vehicle.servicesMaxChargeableDays['1047'])} €&nbsp;
                              <small><Translate content={'vehicleSearch.services.comparesTonTrees'} />
                                {priceFormat(vehicle.pricingData.base.hundredTwentyFiveGramsCOTwo / 100 * vehicle.servicesMaxChargeableDays['1047'] / treePrice)}
                                <Translate content={'vehicleSearch.services.comparesTonTrees2'} /></small>
                        </span>}
                        {reservationLength <= vehicle.servicesMaxChargeableDays['1047'] &&
                            <span>
                          {priceFormat(vehicle.pricingData.base.hundredTwentyFiveGramsCOTwo / 100 * reservationLength)} €&nbsp;
                              <small><Translate content={'vehicleSearch.services.comparesTonTrees'} />
                                {priceFormat(vehicle.pricingData.base.hundredTwentyFiveGramsCOTwo / 100 * reservationLength / treePrice)}
                                <Translate content={'vehicleSearch.services.comparesTonTrees2'} /></small>
                        </span>}
                      </>
                    </LabeledInput>
                    <LabeledInput tooltip={<Translate content={"vehicleSearch.services.co2compensationToolTip1048"} />} type="radio"
                                  id="co2compensation2{vehicle.id}" name="co2compensation"  defaultChecked={services.co2compensation==='1048'} value="1048">
                      <>
                        <Translate content={'vehicleSearch.services.basicCompensation'} />  +
                        {reservationLength > vehicle.servicesMaxChargeableDays['1048'] ?
                            <span>
                          {priceFormat(vehicle.pricingData.base.fiveHundredGramsCOTwo / 100 * vehicle.servicesMaxChargeableDays['1048'])} €&nbsp;
                              <small><Translate content={'vehicleSearch.services.comparesTonTrees'} />
                                {priceFormat(vehicle.pricingData.base.fiveHundredGramsCOTwo / 100 * vehicle.servicesMaxChargeableDays['1048'] / treePrice)}
                                <Translate content={'vehicleSearch.services.comparesTonTrees2'} /></small>
                        </span>
                            :
                            <span>
                          {priceFormat(vehicle.pricingData.base.fiveHundredGramsCOTwo / 100 * reservationLength)} €&nbsp;
                              <small><Translate content={'vehicleSearch.services.comparesTonTrees'} />
                                {priceFormat(vehicle.pricingData.base.fiveHundredGramsCOTwo / 100 * reservationLength / treePrice)}
                                <Translate content={'vehicleSearch.services.comparesTonTrees2'} /></small>
                        </span>
                        }
                      </>
                    </LabeledInput>
                    <LabeledInput tooltip={<Translate content={"vehicleSearch.services.co2compensationToolTip1049"} />} type="radio"
                                  id="co2compensation3{vehicle.id}" name="co2compensation" defaultChecked={services.co2compensation==='1049'} value="1049">
                      <>
                        <Translate content={'vehicleSearch.services.overCompensation'} />  +
                        {reservationLength > vehicle.servicesMaxChargeableDays['1049'] ?
                            <span>
                          <Translate content={'vehicleSearch.services.unlimitedKmDisclaimer'} />
                              {priceFormat(vehicle.pricingData.base.sevenHundredGramsCOTwo / 100 * vehicle.servicesMaxChargeableDays['1049'])} €&nbsp;
                              <small><Translate content={'vehicleSearch.services.comparesTonTrees'} />
                                {priceFormat(vehicle.pricingData.base.sevenHundredGramsCOTwo / 100 * vehicle.servicesMaxChargeableDays['1049'] / treePrice)}
                                <Translate content={'vehicleSearch.services.comparesTonTrees2'} /></small>
                        </span>
                            :
                            <span>
                          {priceFormat(vehicle.pricingData.base.sevenHundredGramsCOTwo / 100 * reservationLength)} €&nbsp;
                              <small><Translate content={'vehicleSearch.services.comparesTonTrees'} />
                                {priceFormat(vehicle.pricingData.base.sevenHundredGramsCOTwo / 100 * reservationLength / treePrice)}
                                <Translate content={'vehicleSearch.services.comparesTonTrees2'} /></small>
                        </span>
                        }
                      </>
                    </LabeledInput>

                    <p className={styles.additionaldriverinstructions}>
                      <Translate content={'vehicleSearch.services.co2compensationInstructions'} />
                    </p>
                  </div>
                </div>
              </>
          }
        </form>
      </div >
      <div className={cx(styles.r24FullWidth, styles.r24Padding10, styles.r24FullWidthFlexPageSelection)} style={{alignItems: "center"}}>
        <div className={cx(styles.r24ChildFlex1, styles.r24RightMargin5)}>
          <button type="button" className={cx("btn", styles.btn, styles.grayButton)} onClick={() =>back()}>
            <ArrowBackIcon className={styles.arrowIcon} />
          </button>
        </div>
        <div className={cx(styles.r24ChildFlex4, styles.r24LeftMargin5)}>
          <button type="submit" className={vehicle.availabilityData?.available ? cx("btn", styles.btn, styles.orangeButton):
              cx("btn", styles.btn, styles.grayButton)} style={{ verticalAlign: "top" }} onClick={validate} >
            { vehicle.availabilityData?.available ?
              translateService.translate('vehicleSearch1.continueToCustomerForm').toUpperCase()
            : translateService.translate('vehicleSearch1.reserved')}
          </button>
        </div>
      </div>
    </div >
  </>
}